import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { useMutationWithError } from "../../../utils/hooks/useMutationWithError";
import { OverlayLoading_atom } from "../../OverlayLoader/atoms";
import { LockedSlots_atom } from "../atoms";
import { LockTimeSlotSeriesInput } from "./__generated__/useLockTimeMutation.graphql";

export const useLockTime = () => {
  const { showToast } = useToast();
  const [lockTime, isLocking] = useMutationWithError(useLockTimeMutationString);
  const updateLockSlotsInfo = useSetAtom(LockedSlots_atom);
  const setLoading = useSetAtom(OverlayLoading_atom);

  const onCompleted = (
    res: any,
    successCallback?: (startSlotId: string, slotIds: string[]) => void
  ) => {
    setLoading(false);
    const slotIds = res.responsePayload.slots.map((slot: any) => slot.id);
    const nowDate = Date.now();
    updateLockSlotsInfo({ slotIds: slotIds, lockTime: nowDate });
    const startSlotId = slotIds[0];
    if (!!successCallback) {
      successCallback(startSlotId, slotIds);
    }
  };

  const onCompletedErrorCustom = (errors: any, errorCallback?: () => void) => {
    setLoading(false);
    const remainingErrors: any = [];
    errors.forEach((e: any) => {
      if (e.code === "already_locked" || e.code === "already_taken") {
        showToast({
          description: t`Selected time is no longer available.`,
          semanticStyle: "error",
          toastId: "tee-time-error",
          testID: "lock-slot-error-toast",
        });
      } else {
        remainingErrors.push(e);
      }
    });

    if (errorCallback) {
      errorCallback();
    }

    return remainingErrors;
  };

  const onErrorCustom = (errorCallback?: () => void) => {
    if (errorCallback) {
      errorCallback();
    }
    setLoading(false);
  };

  const doLockTime = (
    input: LockTimeSlotSeriesInput,
    successCallback?: (startSlotId: string, slotIds: string[]) => void,
    errorCallback?: () => void,
    showOverlay: boolean = true
  ) => {
    if (showOverlay) setLoading(true);

    lockTime({
      variables: { input },
      onCompleted: (res: any) => onCompleted(res, successCallback),
      onCompletedErrorCustom: (errors: any) =>
        onCompletedErrorCustom(errors, errorCallback),
      onErrorCustom: (errorCallback: any) => onErrorCustom(errorCallback),
    });
  };

  return [doLockTime, isLocking] as const;
};

const useLockTimeMutationString = graphql`
  mutation useLockTimeMutation($input: LockTimeSlotSeriesInput!) {
    responsePayload: lockTimeSlotSeries(input: $input) {
      errors {
        code

        message
        field
      }
      success
      slots {
        id
        availability
      }
    }
  }
`;
